import Button from './ui/Button';
import useDailyCountdown from '../hooks/useDailyCountdown';
import { PROMO, PROMO_MESSAGE } from '../config/promo';
import { whatsappLink } from '../config/site';
import { cn } from '../lib/utils';

import championsBg from '../assets/sports/championsleague02.jpg';
import ligue1Bg from '../assets/sports/ligue1d.jpg';
import premierLeagueBg from '../assets/sports/peremierleague.jpg';

import championsLogo from '../assets/league/Logo_Champions_League.png';
import ligue1Logo from '../assets/league/Ligue_1.png';
import premierLeagueLogo from '../assets/league/Premier_League.png';

const BACKDROPS = [
    { src: championsBg, name: 'UEFA Champions League' },
    { src: ligue1Bg, name: 'Ligue 1' },
    { src: premierLeagueBg, name: 'Premier League' },
];

const COMPETITION_LOGOS = [
    { src: championsLogo, name: 'UEFA Champions League' },
    { src: ligue1Logo, name: 'Ligue 1' },
    { src: premierLeagueLogo, name: 'Premier League' },
];

function TimeCell({ value, label }) {
    return (
        <div className="flex min-w-[3.75rem] flex-col items-center rounded-xl border border-line-strong bg-ink-950/70 px-3 py-2.5 backdrop-blur-sm">
            <span className="font-mono text-2xl font-extrabold leading-none text-white tabular-nums sm:text-3xl">
                {String(value).padStart(2, '0')}
            </span>
            <span className="mt-1 text-[0.625rem] font-semibold uppercase tracking-wider text-content-subtle">
                {label}
            </span>
        </div>
    );
}

/**
 * Season-launch promotion banner.
 *
 * Replaces the World Cup 2026 banner, whose countdown targeted a date that has
 * now passed. This one counts down to the next midnight and rolls over on its
 * own, so it never shows an expired timer.
 *
 * The discount is claimed through WhatsApp rather than applied to the listed
 * prices — see src/config/promo.js.
 */
export default function SeasonBanner({ className }) {
    const { hours, minutes, seconds } = useDailyCountdown(PROMO.timeZone);

    return (
        <section
            className={cn(
                'relative overflow-hidden rounded-3xl border border-line-brand',
                className,
            )}
            aria-labelledby="season-banner-title"
        >
            {/* Competition backdrop collage. Decorative only. */}
            <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
                <div className="grid h-full grid-cols-3">
                    {BACKDROPS.map((backdrop) => (
                        <img
                            key={backdrop.name}
                            src={backdrop.src}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className="h-full w-full object-cover"
                        />
                    ))}
                </div>
                {/* Legibility scrim: without this the white copy fails contrast
                    over the brighter areas of the photos. */}
                <div className="absolute inset-0 bg-ink-950/85" />
                <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/75 to-ink-950/90" />
                <div className="absolute -left-24 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-brand/10 blur-[100px]" />
            </div>

            <div className="grid items-center gap-8 p-7 sm:p-9 lg:grid-cols-[1.15fr_1fr] lg:p-11">
                {/* ------------------------------------------------ Left column */}
                <div>
                    <span className="inline-flex items-center gap-2 rounded-full border border-line-brand bg-brand/[0.12] px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-brand">
                        <span className="relative flex h-2 w-2" aria-hidden="true">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-60" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-brand" />
                        </span>
                        {PROMO.label}
                    </span>

                    <h2
                        id="season-banner-title"
                        className="mt-5 text-heading font-extrabold text-white text-balance"
                    >
                        {PROMO.headline} —{' '}
                        <span className="text-brand">−{PROMO.discountEur}€ sur votre forfait</span>
                    </h2>

                    <p className="mt-3 max-w-lg text-content-muted text-pretty">
                        Champions League, Ligue 1 et Premier League en direct et en 4K, plus tout le
                        catalogue films et séries. Contactez-nous sur WhatsApp pour obtenir votre
                        remise avant de commander.
                    </p>

                    {/* Light chips: these league logos are dark, multi-colour PNGs
                        and are illegible directly on the dark backdrop. */}
                    <ul className="mt-6 flex flex-wrap items-center gap-3">
                        {COMPETITION_LOGOS.map((competition) => (
                            <li
                                key={competition.name}
                                className="grid h-14 w-24 place-items-center rounded-xl bg-white px-3 py-2 shadow-card"
                            >
                                <img
                                    src={competition.src}
                                    alt={competition.name}
                                    loading="lazy"
                                    decoding="async"
                                    className="max-h-9 w-full object-contain"
                                />
                            </li>
                        ))}
                    </ul>
                </div>

                {/* ----------------------------------------------- Right column */}
                <div className="rounded-2xl border border-line bg-ink-950/60 p-6 backdrop-blur-sm">
                    <p className="text-center text-xs font-bold uppercase tracking-[0.14em] text-content-subtle">
                        L'offre du jour se termine dans
                    </p>

                    <div
                        className="mt-4 flex justify-center gap-2.5"
                        role="timer"
                        aria-live="off"
                        aria-label={`Offre valable encore ${hours} heures et ${minutes} minutes`}
                    >
                        <TimeCell value={hours} label="Heures" />
                        <TimeCell value={minutes} label="Min" />
                        <TimeCell value={seconds} label="Sec" />
                    </div>

                    <Button
                        href={whatsappLink(PROMO_MESSAGE)}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="lg"
                        fullWidth
                        className="mt-6"
                    >
                        Obtenir mes −{PROMO.discountEur}€
                    </Button>

                    <p className="mt-3 text-center text-xs text-content-subtle">
                        Remise appliquée par notre équipe sur WhatsApp · sans engagement
                    </p>
                </div>
            </div>
        </section>
    );
}
